// apps/buildlog/src/services/firebase.ts

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  limit
} from 'firebase/firestore';

// Firebase Configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

// ===========================
// AUTH SERVICES
// ===========================

export const signIn = async (email: string, password: string): Promise<User> => {
  const { user } = await signInWithEmailAndPassword(auth, email, password);
  return user;
};

export const signUp = async (email: string, password: string): Promise<User> => {
  const { user } = await createUserWithEmailAndPassword(auth, email, password);
  return user;
};

export const signInWithGoogle = async (): Promise<User> => {
  const { user } = await signInWithPopup(auth, googleProvider);
  return user;
};

export const signOut = async (): Promise<void> => {
  await firebaseSignOut(auth);
};

export const getCurrentUser = (): User | null => auth.currentUser;

export { onAuthStateChanged };

// ===========================
// FIRESTORE TYPES
// ===========================

export interface Project {
  id: string;
  name: string;
  description: string;
  userId: string;
  systemPrompt?: string;
  personaId?: string;
  voiceId?: string;
  frameworkId?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  tags?: string[];
  status: 'active' | 'archived' | 'completed';
}

export interface BuildLogEntry {
  id: string;
  projectId: string;
  userId: string;
  title: string;
  content: string;
  timestamp: Timestamp;
  tags?: string[];
  attachments?: string[];

  // Flywheel fields — all optional
  problemSolved?: string;
  outcome?: string;
  techStack?: string[];
  primaryAudience?: 'developer' | 'merchant' | 'investor' | 'consumer' | 'founder';
  canonicalQuestions?: {
    developer?: string;
    merchant?: string;
    investor?: string;
    consumer?: string;
    founder?: string;
  };
  sourceType?: 'manual' | 'archive_import' | 'git_commit';
  published?: boolean;
  publishedAt?: Timestamp;
}

// ===========================
// PROJECT SERVICES
// ===========================

export const getProjects = async (userId: string): Promise<Project[]> => {
  const q = query(
    collection(db, 'projects'),
    where('userId', '==', userId),
    orderBy('updatedAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Project));
};

export const getProject = async (projectId: string): Promise<Project | null> => {
  const snap = await getDoc(doc(db, 'projects', projectId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Project;
};

export const createProject = async (projectData: Omit<Project, 'id'>): Promise<string> => {
  const ref = await addDoc(collection(db, 'projects'), {
    ...projectData,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return ref.id;
};

export const updateProject = async (
  projectId: string,
  updates: Partial<Project>
): Promise<void> => {
  await updateDoc(doc(db, 'projects', projectId), {
    ...updates,
    updatedAt: Timestamp.now(),
  });
};

export const deleteProject = async (projectId: string): Promise<void> => {
  await deleteDoc(doc(db, 'projects', projectId));
};

// ===========================
// BUILD LOG ENTRY SERVICES
// ===========================

// apps/buildlog/src/services/firebase.ts

export async function getLogEntries(userId: string, options?: { limit?: number }): Promise<McpEntry[]> {
  let q = query(
    collection(db, 'buildLogs'),
    where('userId', '==', userId),
    orderBy('timestamp', 'desc')
  );

  if (options?.limit) {
    q = query(q, limit(options.limit));
  }

  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as McpEntry));
}

export const getLogEntry = async (entryId: string): Promise<BuildLogEntry | null> => {
  const snap = await getDoc(doc(db, 'buildLogs', entryId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as BuildLogEntry;
};

export const createLogEntry = async (
  entryData: Omit<BuildLogEntry, 'id'>
): Promise<string> => {
  const ref = await addDoc(collection(db, 'buildLogs'), {
    ...entryData,
    timestamp: Timestamp.now(),
  });
  return ref.id;
};

export const updateLogEntry = async (
  entryId: string,
  updates: Partial<BuildLogEntry>
): Promise<void> => {
  await updateDoc(doc(db, 'buildLogs', entryId), updates);
};

export const deleteLogEntry = async (entryId: string): Promise<void> => {
  await deleteDoc(doc(db, 'buildLogs', entryId));
};

export interface StrategyDoc {
  id: string;
  userId: string;
  title: string;
  content: string;
  type: 'voice' | 'persona' | 'framework';
  tags?: string[];
  updatedAt: any;
}

const DEFAULT_STRATEGY_TEMPLATES = [
  {
    id: 'persona-saas',
    title: 'Persona: Indie SaaS Founder',
    tags: ['saas', 'bip', 'growth'], // Restored tags
    content: '# Role: Growth-focused Founder\n**Focus:** Metrics, pivots, and "The Struggle."'
  },
  {
    id: 'persona-shopify',
    title: 'Persona: Shopify Partner',
    tags: ['shopify', 'ecommerce', 'hydrogen'], // Restored tags
    content: '# Role: Technical Shopify Consultant\n**Focus:** API versions and "Merchant Outcomes."'
  },
  {
    id: 'persona-educator',
    title: 'Persona: Creator-Educator',
    tags: ['education', 'newsletter', 'tutorial'], // Restored tags
    content: '# Role: Tech Educator\n**Focus:** "Aha! Moments" and mental models.'
  },
  {
    id: 'voice-agentic',
    type: 'voice',
    title: 'Voice: The Agentic Frontier',
    content: '# Voice Rules\n* Use "Deterministic" for code, "Probabilistic" for AI.\n* Focus on Intent -> Action -> Observation loops.\n* Tone is scientific and pragmatic.'
  },
  {
    id: 'persona-architect',
    type: 'persona',
    title: 'Persona: Autonomous Architect',
    tags: ['agentic', 'autonomy', 'orchestration'],
    content: '# Professional Lens\n* Goal: Reduce human clicks.\n* Focus: Multi-agent orchestration and safety gates.'
  },
  {
    id: 'framework-trust',
    type: 'framework',
    title: 'Framework: The Trust-to-Autonomy Arc',
    content: '# The Story Arc\n**Act 1:** The manual bottleneck (problemSolved).\n**Act 2:** The agentic intervention (decision logic).\n**Act 3:** The verification (outcome/safety check).'
  },
];

// 1. Fetch user's docs (and auto-seed if empty)
export async function getOrSeedStrategyDocs(userId: string): Promise<StrategyDoc[]> {
  const q = query(collection(db, 'strategyDocs'), where('userId', '==', userId));
  const querySnapshot = await getDocs(q);
  let docs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StrategyDoc));

  // Check if we are missing the Personas specifically
  const hasPersonas = docs.some(d => d.title.startsWith('Persona:'));

  if (!hasPersonas) {
    const newDocs: StrategyDoc[] = [];
    
    // Only seed the missing templates
    const personaTemplates = DEFAULT_STRATEGY_TEMPLATES.filter(t => t.id.startsWith('persona-'));

    for (const template of personaTemplates) {
      const docRef = await addDoc(collection(db, 'strategyDocs'), {
        userId,
        title: template.title,
        content: template.content,
        tags: template.tags || [],
        updatedAt: serverTimestamp(),
      });
      
      newDocs.push({
        id: docRef.id,
        userId,
        title: template.title,
        content: template.content,
        tags: template.tags || [],
        type: (template.type as 'voice' | 'persona' | 'framework') || 'persona',
        updatedAt: new Date(),
      });
    }
    docs = [...docs, ...newDocs];
  }

  return docs;
}

// 2. Save document edits
export const updateStrategyDoc = async (
  docId: string, 
  content: string, 
  tags: string[] = [], // 3rd argument
  type?: 'voice' | 'persona' | 'framework' // 4th argument (The fix)
): Promise<void> => {
  const docRef = doc(db, 'strategyDocs', docId);
  
  // Build the update object dynamically so we only overwrite type if it's provided
  const updates: any = { 
    content, 
    tags, 
    updatedAt: serverTimestamp() 
  };
  
  if (type) {
    updates.type = type;
  }
  
  await updateDoc(docRef, updates);
};

export const createStrategyDoc = async (
  userId: string, 
  title: string,
  type: 'voice' | 'persona' | 'framework' = 'persona' // 3rd argument (The fix)
): Promise<StrategyDoc> => {
  
  const docRef = await addDoc(collection(db, 'strategyDocs'), {
    userId,
    title,
    content: '',
    tags: [],
    type, // Save it to the database
    updatedAt: serverTimestamp(),
  });
  
  return {
    id: docRef.id,
    userId,
    title,
    content: '',
    tags: [],
    type, // Return it to the UI
    updatedAt: new Date(),
  };
};

export async function deleteStrategyDoc(docId: string): Promise<void> {
  await deleteDoc(doc(db, 'strategyDocs', docId));
}

// ─── CONTENT DRAFTS ─────────────────────────────────────────────────────────

export interface ContentDraft {
  id: string;
  userId: string;
  platform: 'X' | 'linkedin' | 'blog' | 'newsletter' | 'other';
  content: string;
  projectId?: string;
  sourceEntryIds?: string[];
  status: 'draft' | 'published';
  createdAt: any;
  updatedAt: any;
}

export async function getContentDrafts(userId: string): Promise<ContentDraft[]> {
  const q = query(
    collection(db, 'contentDrafts'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ContentDraft));
}

export async function deleteContentDraft(draftId: string): Promise<void> {
  await deleteDoc(doc(db, 'contentDrafts', draftId));
}

export type McpEntry = BuildLogEntry;